import React from 'react';
import { Typography, Button, Paper } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { getLocalizedString } from "@icarius-localization/strings";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '400px',
    width: '100%',
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 500,
    textAlign: 'center',
    backgroundColor: 'var(--secondaryBackgroundColor)',
    color: 'var(--mainText)',
  },
  icon: {
    fontSize: 64,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    color: 'var(--mainText)',
  },
  message: {
    marginBottom: theme.spacing(4),
    color: 'var(--mainText)',
    whiteSpace: 'pre-line',
  },
}));

const FallbackComponent = ({ error, resetError }) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={3}>
        <ErrorOutlineIcon className={classes.icon} />
        <Typography variant="h5" className={classes.title}>
          {getLocalizedString("errorBoundaryTitle")}
        </Typography>
        <Typography variant="body1" className={classes.message}>
          {getLocalizedString("errorBoundaryMessage")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (resetError) {
              resetError();
            } else {
              window.location.reload();
            }
          }}
          className={classes.button}
        >
          {getLocalizedString("errorBoundaryRetryButton")}
        </Button>
      </Paper>
    </div>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  }

  componentDidCatch(error, errorInfo) {
    // Cleanup any lingering Material-UI body locks in case a Modal/Dialog crashed
    document.body.style.overflow = 'auto';
    document.body.style.pointerEvents = 'auto';
    document.body.style.paddingRight = '0px';

    // Remove invisible react-error-overlay iframes that intercept all clicks in development
    if (process.env.NODE_ENV === 'development') {
      const interval = setInterval(() => {
        document.querySelectorAll('body > iframe').forEach(iframe => {
          iframe.style.pointerEvents = 'none';
          iframe.style.display = 'none';
        });
      }, 100);

      setTimeout(() => clearInterval(interval), 5000);
    }
  }

  render() {
    if (this.state.hasError) {
      return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
